import { uniqBy } from "lodash";
import { LatLng } from "geolocation-utils";

import { Utils } from "utils";
import { IPlace, IRouteDay, Iterable } from "types";
import { DEFAULT_ROUTE_DAY_HOURS } from "constants/constants";

import { Road } from "../road";

export class RouteChunk {
  constructor(
    private coordinates: LatLng[],
    private places: (Iterable<IPlace> | undefined)[],
    private waypoints: LatLng[]
  ) {
    this.places = places;
    this.waypoints = waypoints;
    this.coordinates = coordinates;
  }

  /* Public Methods */

  public chunk() {
    /* Chunk full route by days */
    const days = this.generateDays();
    // return days;
    /* Try to combine some small days with previus one's  (if needed) */
    const combinedDays = this.combineDays(days);
    return combinedDays;
  }

  /* Private Methods */

  private generateDays() {
    const days: IRouteDay[] = [];

    if (this.coordinates.length === 0 || this.waypoints.length === 0) {
      return days;
    }

    let dayTime = 0;
    let dayDistance = 0;
    let dayWaypoints: LatLng[] = [];
    let dayCoordinates: number[][] = [];
    let dayPlaces: (Iterable<IPlace> | undefined)[] = [];

    const emptyData = {
      hotels: [],
      campings: [],
      allPlaces: [],
      sameRoads: [],
      suggestedPlaces: [],
    };

    const createNewDay = () => {
      days.push({
        ...emptyData,
        totalTime: dayTime,
        totalDistance: dayDistance,
        points: [...dayCoordinates],
        selectedPlaces: [...dayPlaces],
        selectedWaypoints: [...dayWaypoints],
      });
    };

    let previusIndex = 0;

    for (const [index, currentWaypoint] of this.waypoints.entries()) {
      const currentPlace = this.places.at(index);
      const prevWaypoint = this.waypoints.at(index - 1);

      const breaktime = currentPlace?.breaktime ? currentPlace.breaktime : 0;

      const fullRoad = Road.fromLatLng(this.coordinates);

      /* Find index in coordinates of waypoint */
      let currentIndex = fullRoad.findIndexOf(currentWaypoint);
      if (previusIndex > currentIndex) {
        currentIndex = fullRoad.findIndexOf(currentWaypoint, "last");
      }

      const roadSlice = this.coordinates.slice(previusIndex, currentIndex + 1);
      previusIndex = currentIndex;

      const currentChunkRoad = Road.fromLatLng(roadSlice);
      const pathTimeInMinutes = currentChunkRoad.getTime("minutes");
      const pathDistance = currentChunkRoad.getDistance("m");

      const maxDayTime = 9.5 * 60; // minutes
      const isTimeExceeded = pathTimeInMinutes + dayTime > maxDayTime;

      if (isTimeExceeded && currentPlace) {
        // Substract last place breaktime, because it's night place;
        dayTime = dayTime - (dayPlaces.filter(Boolean).at(-1)?.breaktime ?? 0);
        createNewDay();

        dayTime = pathTimeInMinutes + breaktime;
        dayDistance = pathDistance;
        dayPlaces = [this.places.at(index - 1), currentPlace];
        dayWaypoints = [prevWaypoint!, currentWaypoint];
        dayCoordinates = currentChunkRoad.coordinates.map(
          Utils.Coordinates.convert.latLng.toPoint
        );

        continue;
      }

      dayDistance += pathDistance;
      dayTime += pathTimeInMinutes + breaktime;
      dayPlaces = [...dayPlaces, currentPlace];
      dayWaypoints = [...dayWaypoints, currentWaypoint];
      dayCoordinates = [
        ...dayCoordinates,
        ...currentChunkRoad.coordinates.map(
          Utils.Coordinates.convert.latLng.toPoint
        ),
      ];

      if (index === this.waypoints.length - 1) {
        createNewDay();
      }
    }

    return days;
  }

  private combineDays(routeDays: IRouteDay[]) {
    let reducedDays: typeof routeDays = [];

    for (const [index, currentDay] of routeDays.entries()) {
      const minDayTime = 120; // 2 hours;
      const maxDayTime = DEFAULT_ROUTE_DAY_HOURS * 60;

      if (index !== 0) {
        if (currentDay.totalTime <= minDayTime) {
          const prevDay = routeDays[index - 1];
          if (prevDay.totalTime + currentDay.totalTime <= maxDayTime) {
            const totalPoints = [...prevDay.points, ...currentDay.points];
            const places = uniqBy(
              [...prevDay.selectedPlaces, ...currentDay.selectedPlaces],
              (item) => {
                if (item === undefined) return true;
                return item.place_id;
              }
            );

            const road = Road.fromPoints(totalPoints);
            const pathDistance = road.getDistance("m");
            const pathTime = road.getTime("minutes");
            const breaktimes = places.reduce(
              (accumulator, current, currentIndex, array) => {
                if (currentIndex === 0) return accumulator;
                if (currentIndex === array.length - 1) return accumulator;
                return accumulator + (current?.breaktime ?? 0);
              },
              0
            );

            reducedDays = reducedDays.map((item, i) =>
              i === index - 1
                ? {
                    ...item,
                    points: totalPoints,
                    selectedPlaces: places,
                    totalDistance: pathDistance,
                    totalTime: pathTime + breaktimes,
                  }
                : item
            );
            continue;
          }
        }
      }

      reducedDays.push(currentDay);
    }

    return reducedDays;
  }
}
